import React, { useState, useEffect } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import { useTranslation } from 'react-i18next';
import '../pagination/pagination.css';
import Pagination from '../pagination/pagination';
import LeaseApiService from '../../services/apiService';
import { IoAddSharp } from 'react-icons/io5';
import { RiRefreshLine } from 'react-icons/ri';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import Select from 'react-select';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { getName } from '../../../Redux/selecters';
import { fetchCommunityCountries, fetchCommunityCitiesByCountry, resolveCommunityRecordId } from './communityApiHelpers';
import CreateCommunity from './createCommunity';
import UpdateCommunity from './updateCommunity';
import RowActions from '../shared/rowActions';
import SummaryStrip, { countDistinct, totalFrom } from '../shared/summaryStrip';
import '../customerMaster/customerListing.css';
import { FiPlus, FiRotateCcw, FiSearch } from 'react-icons/fi';
import RangeChips from '../shared/rangeChips';
import { useRange, fetchRanged, useCardFilter, valueTiles } from '../shared/rangedList';

const emptyValueFormatter = (params) => {
  const v = params.value;
  if (v === null || v === undefined || v === '' || v === '0') return '--';
  return v;
};

const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
    color: '#000',
    ':hover': {
      backgroundColor: '#F5F5F5',
      color: '#000 ',
    },
  }),
};

const CommunityListing = () => {
  const { t } = useTranslation();
  const apiService = new LeaseApiService();
  const userName = useSelector(getName);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState();
  const [FilterTab, setFilterTab] = useState(true);
  const [contextMenu, setContextMenu] = useState(null);
  const [countriesList, setCountriesList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [formModal, setFormModal] = useState({ mode: null, data: null });
  const [formData, setFormData] = useState({
    communityNo: '',
    description: '',
    city: null,
    country: null,
  });

  useEffect(() => {
    fetchDropdownData();
    getItems();
  }, []);

  useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchDropdownData = async () => {
    try {
      const countries = await fetchCommunityCountries(apiService);
      setCountriesList(countries);
    } catch (error) {
      console.error('Error fetching community dropdown data:', error);
    }
  };

  const handleCountryChange = async (selectedOption) => {
    setFormData((prev) => ({ ...prev, country: selectedOption, city: null }));
    if (!selectedOption?.label) {
      setCityList([]);
      return;
    }
    try {
      const cities = await fetchCommunityCitiesByCountry(apiService, selectedOption.label);
      setCityList(cities);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setCityList([]);
    }
  };

  const { range, setRange, cache: rangeCache } = useRange(() => getItems(1));
  const cf = useCardFilter(rowData, range, rangeCache);
  const getItems = async (newPageNo = pagination?.currentPage) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('PageNo', newPageNo || 1);
      params.append('PageSize', pageSize || 20);
      if (formData.communityNo) params.append('CommunityNo', formData.communityNo);
      if (formData.description) params.append('Description', formData.description);
      if (formData.city?.label) params.append('City', formData.city.label);
      if (formData.country?.label) params.append('Country', formData.country.label);
      const res = await fetchRanged({ apiService, endpoint: 'GET_COMMUNITY_LIST', params, range, cache: rangeCache, pageNo: newPageNo, pageSize });
      setRowData(res?.data?.result || []);
      setPagination(res?.data?.paginationData);
    } catch (error) {
      console.error('Error fetching communities:', error);
      toast.error(t('communityMaster.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handlenew = () => {
    setFormModal({ mode: 'create', data: null });
  };

  const handleEdit = (rowsData) => {
    const recordId = resolveCommunityRecordId(rowsData);
    if (!recordId) {
      toast.warning(t('communityMaster.missingRecordIdEdit'));
      return;
    }
    setFormModal({ mode: 'edit', data: rowsData });
  };

  const handleView = (rowsData) => {
    const recordId = resolveCommunityRecordId(rowsData);
    if (!recordId) {
      toast.warning(t('communityMaster.missingRecordIdView'));
      return;
    }
    setFormModal({ mode: 'view', data: rowsData });
  };

  const closeFormModal = () => {
    setFormModal({ mode: null, data: null });
  };

  const handleFormSaved = () => {
    closeFormModal();
    getItems(1);
  };

  const handleDelete = async (rowsData) => {
    const recordId = resolveCommunityRecordId(rowsData);
    const communityNo = rowsData?.communityNo ?? rowsData?.CommunityNo ?? '';
    if (!window.confirm(`Delete community ${communityNo || recordId}?`)) return;
    try {
      const body = {
        recordId: String(recordId || '0'),
        communityNo: String(communityNo || '0'),
        modifiedBy: userName || '',
      };
      const res = await apiService.delete('DELETE_COMMUNITY', body);
      if (res?.data?.isSuccessful || res?.data?.statusCode === 200 || res?.data?.statusCode === 201) {
        toast.success(res?.data?.message || t('communityMaster.deleteSuccess'));
        getItems();
      } else {
        toast.warning(res?.data?.message || t('communityMaster.deleteFailed'));
      }
    } catch (error) {
      console.error('Error deleting community:', error);
      toast.error(t('communityMaster.deleteFailed'));
    }
  };

  const handleMenuAction = (action, data) => {
    if (action === 'edit') handleEdit(data);
    if (action === 'view') handleView(data);
    if (action === 'delete') handleDelete(data);
    setContextMenu(null);
  };

  const handleClearFilters = () => {
    setFormData({ communityNo: '', description: '', city: null, country: null });
    setCityList([]);
    getItems(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDropDownChange = ({ name, value }) => {
    if (name === 'city') {
      setFormData((prev) => ({ ...prev, city: value }));
      return;
    }
    handleCountryChange(value);
  };

  const columnDefs = [
    {
      headerName: t('common.actions'),
      field: 'record_Id',
      cellRenderer: BtnCellRenderer,
      pinned: 'left',
      width: 140,
      suppressSizeToFit: true,
      valueGetter: (p) => resolveCommunityRecordId(p.data),
    },
    {
      headerName: t('communityMaster.columns.communityNo'),
      field: 'communityNo',
      filter: true,
      floatingFilter: true,
      suppressMovable: true,
      flex: 1,
      minWidth: 140,
      valueGetter: (p) => p.data?.communityNo ?? p.data?.CommunityNo ?? '',
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('communityMaster.columns.description'),
      field: 'description',
      filter: true,
      floatingFilter: true,
      suppressMovable: true,
      flex: 2,
      minWidth: 200,
      valueGetter: (p) => p.data?.description ?? p.data?.Description ?? '',
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('communityMaster.columns.marketingDescription'),
      field: 'marketingDescription',
      filter: true,
      floatingFilter: true,
      suppressMovable: true,
      flex: 2,
      minWidth: 200,
      valueGetter: (p) => p.data?.marketingDescription ?? p.data?.MarketingDescription ?? '',
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('communityMaster.columns.remarks'),
      field: 'remarks',
      filter: true,
      floatingFilter: true,
      suppressMovable: true,
      flex: 2,
      minWidth: 200,
      valueGetter: (p) => p.data?.remarks ?? p.data?.Remarks ?? '',
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('communityMaster.columns.city'),
      field: 'city',
      filter: true,
      floatingFilter: true,
      suppressMovable: true,
      flex: 1,
      minWidth: 140,
      valueFormatter: emptyValueFormatter,
    },
    {
      headerName: t('communityMaster.columns.country'),
      field: 'country',
      filter: true,
      floatingFilter: true,
      suppressMovable: true,
      flex: 1,
      minWidth: 140,
      valueFormatter: emptyValueFormatter,
    },
  ];

  const onGridReady = (params) => {
    params.api.sizeColumnsToFit();
  };

  return (
    <div className="community-list-page cl-page">
      <div className="cl-titlebar">
        <div className="cl-title">
        <h1>{t('communityMaster.pageTitle')}</h1>
        <p>Communities group your properties by area. Search, review and manage them here.</p>
      </div>
        <RangeChips value={range} onChange={setRange} />
      </div>
      <SummaryStrip
        label="Total communities"
        total={totalFrom(pagination, rowData.length)}
        subtitle={`${countDistinct(cf.statsRows, 'city')} cities`}
        heroNote={cf.inPeriod ? 'Chosen period' : 'All records'} heroActive={!cf.filter} onHero={() => cf.setFilter(null)}
        tiles={[
          { label: cf.inPeriod ? 'In period' : 'On this page', value: cf.statsRows.length, note: 'communities', active: !cf.filter, onClick: () => cf.setFilter(null) },
          ...valueTiles(cf, 'country', 2),
          ...valueTiles(cf, 'city', 2),
        ]}
        ariaLabel="Community summary"
      />
      <section className="cl-panel cl-filters" aria-label={t('common.searchParameters')}>
        <div>
          <div>
            <div className="form-group grid-contain ">
              <div className="d-flex">
                <label className="form-label pt-2 start">{t('communityMaster.filters.communityNo')}</label>
                <input type="text" className="p-2 form-control w-75" name="communityNo" value={formData.communityNo} onChange={handleInputChange} />
              </div>
              <div className="d-flex">
                <label className="form-label pt-2 start">{t('communityMaster.filters.description')}</label>
                <input type="text" className="p-2 form-control w-75" name="description" value={formData.description} onChange={handleInputChange} />
              </div>
              <div className="d-flex">
                <label className="form-label pt-2 start">{t('communityMaster.filters.country')}</label>
                <Select
                  className="p-0 w-100"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                  value={formData.country}
                  onChange={(selectedOption) => handleCountryChange(selectedOption)}
                  options={countriesList?.map((item) => ({ value: item, label: item?.text }))}
                  isClearable
                />
              </div>
              <div className="d-flex">
                <label className="form-label pt-2 start">{t('communityMaster.filters.city')}</label>
                <Select
                  className="p-0 w-100"
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  theme={(theme) => ({ ...theme, borderRadius: 5, padding: 9 })}
                  value={formData.city}
                  onChange={(selectedOption) => handleDropDownChange({ name: 'city', value: selectedOption })}
                  options={cityList?.map((item) => ({ value: item, label: item?.text }))}
                  isDisabled={!formData.country}
                  isClearable
                />
              </div>
              <div className="cl-filter-actions">
                <button type="button" className="cl-btn cl-btn-ghost" onClick={handleClearFilters}><FiRotateCcw size={16} />{t('common.reset')}</button>
                <button type="button" className="cl-btn cl-btn-primary" onClick={() => getItems()}><FiSearch size={16} />{t('common.search')}</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cl-panel cl-list">
        {loading ? (
          <div>{t('common.loading')}</div>
        ) : (
          <>
            <div className="cl-list-head">
              <h2>{t('communityMaster.pageTitle')}</h2>
              <button type="button" className="cl-btn cl-btn-primary" onClick={handlenew}><FiPlus size={16} />{t('common.add')}</button>
            </div>
            <div className="cl-grid-wrap" style={{ width: '100%' }}>
              <div className="ag-theme-quartz customer-grid" style={{ height: 650, width: '100%' }}>
                <AgGridReact
                  rowData={cf.filter ? cf.shownRows : rowData}
                  enableFilter={true}
                  floatingFilter={true}
                  columnDefs={columnDefs}
                  onGridReady={onGridReady}
                  onFirstDataRendered={onGridReady}
                  frameworkComponents={{ BtnCellRenderer }}
                  context={{ onView: handleView, onEdit: handleEdit, onDelete: handleDelete }}
                />
              </div>
              {!cf.filter && <Pagination pageSize={pageSize} setPageSize={setPageSize} pagination={pagination} getItems={getItems} />}
            </div>
          </>
        )}
      </section>

      <Modal
        show={!!formModal.mode}
        onHide={closeFormModal}
        backdrop="static"
        keyboard={false}
        centered
        dialogClassName="community-form-modal"
        contentClassName="p-0"
      >
        <Modal.Body style={{ padding: 0 }}>
          {formModal.mode === 'create' && <CreateCommunity onClose={closeFormModal} onSaved={handleFormSaved} />}
          {(formModal.mode === 'edit' || formModal.mode === 'view') && (
            <UpdateCommunity
              initialData={formModal.data}
              viewOnly={formModal.mode === 'view'}
              onClose={closeFormModal}
              onSaved={handleFormSaved}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

// Row actions: shared kebab menu; handlers come from the grid context.
const BtnCellRenderer = ({ data, context }) => (
  <RowActions data={data} onView={context?.onView} onEdit={context?.onEdit} onDelete={context?.onDelete} canDelete />
);

export default CommunityListing;
